import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Pencil,
  Send,
  Archive,
  Trash2,
  Loader2,
  FileText,
  Users,
  Clock,
  CalendarPlus,
  CalendarCheck,
  Target,
  Info,
} from "lucide-react";
import type { ElementType } from "react";
import { PermissaoEnum } from "@workflow/types";

import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { PageTabs } from "@/components/shared/PageTabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { JobDetailItem } from "@/components/shared/job-header/JobDetailItem";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/badge-status";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MarkdownRenderer from "@/components/ui/markdown-reader";
import { withPermissionPage } from "@/components/hocs/WithPermissionPage";
import { cn } from "@/lib/utils";

import { comunicadoSistemaQueries } from "@/services/api/queries/comunicado-sistema.queries";
import { useComunicadosSistemaAdmin } from "@/hooks/comunicados/useComunicadosSistemaAdmin";
import {
  NIVEL_CONFIG,
  ESTADO_COLOR,
  ESTADO_LABEL,
} from "@/components/comunicados/comunicado-nivel";
import { ComunicadoSistemaNivel } from "@/types/comunicado-sistema.type";
import { cargoLabel } from "@/pages/admin/aprovacoes/aprovacao-config-options";
import { areaLabel } from "./comunicado-segmentacao-options";
import { ComunicadoAceitesPanel } from "./components/ComunicadoAceitesPanel";

const LISTA = "/admin/comunicados";

const fmtDataHora = (d?: string) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" }) : "—";

/** Aparência do hero por nível (gradiente + caixa do ícone + faixa de accent). */
const HERO: Record<ComunicadoSistemaNivel, { gradient: string; iconBox: string; accent: string }> =
  {
    CRITICO: {
      gradient: "from-red-500/[0.08] via-card to-card",
      iconBox: "bg-red-100 text-red-700 ring-red-500/20 dark:bg-red-900/40 dark:text-red-300",
      accent: "bg-red-500",
    },
    ALERTA: {
      gradient: "from-amber-500/[0.08] via-card to-card",
      iconBox:
        "bg-amber-100 text-amber-700 ring-amber-500/20 dark:bg-amber-900/40 dark:text-amber-300",
      accent: "bg-amber-500",
    },
    INFORMATIVO: {
      gradient: "from-blue-500/[0.08] via-card to-card",
      iconBox: "bg-blue-100 text-blue-700 ring-blue-500/20 dark:bg-blue-900/40 dark:text-blue-300",
      accent: "bg-blue-500",
    },
  };

function SectionHeader({ icon: Icon, title }: { icon: ElementType; title: string }) {
  return (
    <CardHeader className="flex-row items-center gap-2 space-y-0 pb-3">
      <span className="grid size-7 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
  );
}

/**
 * Resumo de aceites no hero: o admin abre o detalhe do crítico para saber quem
 * falta, então o número aparece antes da aba. Usa a mesma queryKey do painel —
 * o cache do react-query serve as duas, sem requisição extra.
 */
function AceitesResumoHero({ comunicadoId }: { comunicadoId: number }) {
  const { data } = useQuery(comunicadoSistemaQueries.pendentes(comunicadoId));
  if (!data || data.total === 0) return null;

  const pct = Math.round((data.aceitosCount / data.total) * 100);
  const completo = data.pendentesCount === 0;

  return (
    <div className="shrink-0 rounded-xl border border-border/60 bg-card/70 px-4 py-3 text-center backdrop-blur-sm">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Confirmaram
      </p>
      <p className="text-xl font-bold tabular-nums text-foreground">
        {data.aceitosCount}
        <span className="text-sm font-medium text-muted-foreground">/{data.total}</span>
      </p>
      <p
        className={cn("text-[11px] font-medium", completo ? "text-emerald-600" : "text-amber-600")}
      >
        {completo ? "todos leram" : `faltam ${data.pendentesCount}`}
      </p>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", completo ? "bg-emerald-500" : "bg-amber-500")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function iniciais(nome?: string): string {
  if (!nome) return "—";
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "—";
}

const ComunicadoDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const comunicadoId = Number(id);
  const { data: c, isLoading } = useQuery(comunicadoSistemaQueries.detail(comunicadoId));
  const { publicar, arquivar, remover } = useComunicadosSistemaAdmin();
  const [confirmar, setConfirmar] = useState(false);

  if (isLoading && !c) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando comunicado...
      </div>
    );
  }

  if (!c) {
    return (
      <TopBarSection
        entity="Comunicado"
        title="Comunicado não encontrado"
        showButton
        buttonLabel="Voltar"
        buttonIcon={<ArrowLeft />}
        onButtonClick={() => navigate(LISTA)}
      />
    );
  }

  const config = NIVEL_CONFIG[c.nivel];
  const hero = HERO[c.nivel];
  const { Icon } = config;
  const publicado = c.status === "PUBLICADO";

  const cargos = c.alvos.filter((a) => a.tipo === "CARGO").map((a) => cargoLabel(a.valor));
  const areas = c.alvos.filter((a) => a.tipo === "AREA").map((a) => areaLabel(a.valor));
  const publicoAlvo = [...cargos, ...areas];

  const detalhes = (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Conteúdo (coluna larga) */}
      <Card className="lg:col-span-2">
        <SectionHeader icon={FileText} title="Conteúdo" />
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3.5">
            <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <Info className="h-3.5 w-3.5" /> Resumo
            </p>
            <p className="text-sm leading-relaxed text-foreground">{c.resumo}</p>
          </div>

          {c.conteudo ? (
            <MarkdownRenderer content={c.conteudo} />
          ) : (
            <p className="py-6 text-center text-sm italic text-muted-foreground">
              Sem conteúdo detalhado.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Informações (coluna estreita) */}
      <Card className="h-fit lg:col-span-1">
        <SectionHeader icon={Info} title="Informações" />
        <CardContent className="space-y-4">
          <JobDetailItem
            label="Criado por"
            value={c.criadoPorNome ?? "—"}
            leading={
              <span className="grid size-7 place-items-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                {iniciais(c.criadoPorNome)}
              </span>
            }
          />
          <JobDetailItem label="Criado em" value={fmtDataHora(c.criadoEm)} icon={Clock} />
          <JobDetailItem
            label="Início da vigência"
            value={fmtDataHora(c.inicioEm)}
            icon={CalendarPlus}
          />
          <JobDetailItem
            label="Fim da vigência"
            value={c.fimEm ? fmtDataHora(c.fimEm) : "Sem prazo"}
            icon={CalendarCheck}
          />
          <div className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0 text-muted-foreground">
              <Target className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Público-alvo
              </p>
              {publicoAlvo.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {publicoAlvo.map((alvo) => (
                    <span
                      key={alvo}
                      className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80"
                    >
                      {alvo}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-medium text-foreground">Todos os usuários</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="Comunicado"
        title={c.titulo}
        showButton
        buttonLabel="Voltar"
        buttonIcon={<ArrowLeft />}
        onButtonClick={() => navigate(LISTA)}
        extraActions={[
          <Button
            key="editar"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => navigate(`/admin/comunicados/${c.id}/editar`)}
          >
            <Pencil className="h-4 w-4" /> Editar
          </Button>,
          publicado ? (
            <Button
              key="arquivar"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => arquivar.mutate(c.id)}
            >
              <Archive className="h-4 w-4" /> Arquivar
            </Button>
          ) : (
            <Button
              key="publicar"
              size="sm"
              className="gap-1.5"
              onClick={() => publicar.mutate(c.id)}
            >
              <Send className="h-4 w-4" /> Publicar
            </Button>
          ),
          <Button
            key="excluir"
            size="sm"
            variant="outline"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setConfirmar(true)}
          >
            <Trash2 className="h-4 w-4" /> Excluir
          </Button>,
        ]}
      />

      {/* Hero */}
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br p-5 shadow-sm",
          hero.gradient,
        )}
      >
        <span className={cn("absolute inset-y-0 left-0 w-1", hero.accent)} />
        <div className="flex items-start gap-4">
          <span
            className={cn(
              "grid size-12 shrink-0 place-items-center rounded-xl ring-1",
              hero.iconBox,
            )}
          >
            <Icon className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase",
                  config.badge,
                )}
              >
                {config.label}
              </span>
              <StatusBadge color={ESTADO_COLOR[c.estado]} className="font-medium">
                {ESTADO_LABEL[c.estado]}
              </StatusBadge>
            </div>
            <h2 className="truncate text-xl font-semibold text-foreground">{c.titulo}</h2>
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{c.resumo}</p>
          </div>

          {c.nivel === "CRITICO" && publicado && <AceitesResumoHero comunicadoId={c.id} />}
        </div>
      </div>

      <PageTabs
        paramKey="tab"
        tabs={[
          { value: "detalhes", label: "Detalhes", icon: FileText, content: detalhes },
          {
            value: "aceites",
            label: "Aceites",
            icon: Users,
            visible: c.nivel === "CRITICO",
            content: (
              <Card>
                <SectionHeader icon={Users} title="Confirmações de leitura" />
                <CardContent>
                  <ComunicadoAceitesPanel comunicadoId={c.id} fimEm={c.fimEm} />
                </CardContent>
              </Card>
            ),
          },
        ]}
      />

      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir comunicado?</AlertDialogTitle>
            <AlertDialogDescription>
              “{c.titulo}” será removido definitivamente. Para apenas tirá-lo do ar, use Arquivar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => remover.mutate(c.id, { onSuccess: () => navigate(LISTA) })}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default withPermissionPage(ComunicadoDetailsPage, [PermissaoEnum.COMUNICADO_SISTEMA_MANAGE]);
